import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { uploadAudienceList } from "../../store/actions/broadcasts/broadcastActions";
import { errorMessages } from "./broadcastFormHelpers";

/**
 * Audience -> "Employee list (CSV)" on a form item (SPEC 4.3 rule 5, 6.2). One column
 * `emp_num`; the server replaces the item's employee rules and answers {matched, unmatched}.
 *
 *   <EmployeeListUpload itemId listCount />
 *
 * The list is stored against the saved item, so a form that is not saved yet only gets a
 * "save first" line. The count shown starts from the item's rules (listCount) and follows the
 * last upload.
 */

export const NOT_CSV = "Choose a CSV file";
export const UPLOAD_FAILED = "The list could not be uploaded";

const CSV_MIMES = ["text/csv", "application/csv"];

// By name or by MIME: some browsers report no type at all for a .csv.
export const isCsv = (file) =>
  !!file && (/\.csv$/i.test(String(file.name || "")) || CSV_MIMES.includes(String(file.type || "")));

class EmployeeListUpload extends Component {
  state = {
    file: null,
    error: null,      // client refusal or the server's messages
    result: null,     // {matched, unmatched} of the last upload
    count: Number(this.props.listCount) || 0,
    uploading: false,
  };

  componentWillUnmount() {
    this.unmounted = true;
  }

  onFile = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (file && !isCsv(file)) {
      this.setState({ file: null, error: [NOT_CSV] });
      return;
    }
    this.setState({ file: file, error: null });
  };

  onUpload = () => {
    const { file } = this.state;
    if (!file || this.state.uploading) return;
    this.setState({ uploading: true, error: null, result: null });
    this.props.uploadAudienceList(this.props.itemId, file)
      .then((result) => {
        if (this.unmounted) return;
        const matched = Number(result && result.matched) || 0;
        this.setState({ uploading: false, result: { matched: matched, unmatched: (result && result.unmatched) || [] }, count: matched });
      })
      .catch((e) => {
        if (this.unmounted) return;
        const messages = e && e.status === 422 ? errorMessages(e) : [UPLOAD_FAILED];
        this.setState({ uploading: false, error: messages });
      });
  };

  renderResult = () => {
    const { result } = this.state;
    if (!result) return null;
    const unmatched = Array.isArray(result.unmatched) ? result.unmatched : [];
    return (
      <div role="status" className="broadcast-list-result">
        <div>{"Matched " + result.matched + " employees"}</div>
        <div>{unmatched.length ? "Not found: " + unmatched.join(", ") : "All employee numbers matched"}</div>
      </div>
    );
  };

  render() {
    const { itemId } = this.props;
    const { file, error, count, uploading } = this.state;
    if (itemId === null || itemId === undefined || itemId === "") {
      return <p className="text-muted">Save first to upload an employee list</p>;
    }
    return (
      <div className="broadcast-employee-list">
        <p>{count > 0 ? count + " employees on the list" : "No employee list uploaded"}</p>
        {error ? (
          <div role="alert" className="alert alert-danger">
            <ul className="mb-0">{error.map((m, i) => <li key={i}>{m}</li>)}</ul>
          </div>
        ) : null}
        <div className="form-group">
          <label htmlFor="employee-list-file">Employee list (CSV)</label>
          <input type="file" className="form-control-file" id="employee-list-file" accept=".csv" disabled={uploading} onChange={this.onFile} />
          <small className="form-text text-muted">One column headed emp_num. Uploading replaces the current list.</small>
        </div>
        <Button variant="outline-primary" type="button" disabled={!file || uploading} onClick={this.onUpload}>Upload list</Button>
        {this.renderResult()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    uploadAudienceList: (id, file) => dispatch(uploadAudienceList(id, file)),
  };
};

export default connect(null, mapDispatchToProps)(EmployeeListUpload);
